import { create } from "zustand";
import { CheckCircle2, AlertTriangle, Info, XCircle, LucideIcon } from "lucide-react";

export type ToastVariant = "success" | "error" | "warning" | "info";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: string;
  title: string;
  description?: string;
  variant: ToastVariant;
  duration: number;
  action?: ToastAction;
  dismissed?: boolean;
}

interface ToastState {
  toasts: Toast[];
  push: (toast: Omit<Toast, "id" | "duration"> & { duration?: number }) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: ({ title, description, variant, duration = 4000, action }) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    set((s) => ({
      toasts: [...s.toasts, { id, title, description, variant, duration, action }],
    }));
    return id;
  },
  dismiss: (id) =>
    set((s) => ({
      toasts: s.toasts.filter((t) => t.id !== id),
    })),
  clear: () => set({ toasts: [] }),
}));

export const toast = {
  success: (title: string, description?: string, action?: ToastAction) =>
    useToastStore.getState().push({ title, description, variant: "success", action, duration: action ? 6000 : 4000 }),
  error: (title: string, description?: string) =>
    useToastStore.getState().push({ title, description, variant: "error", duration: 6000 }),
  warning: (title: string, description?: string) =>
    useToastStore.getState().push({ title, description, variant: "warning", duration: 5000 }),
  info: (title: string, description?: string) =>
    useToastStore.getState().push({ title, description, variant: "info" }),
  dismiss: (id: string) => useToastStore.getState().dismiss(id),
};

export const TOAST_ICONS: Record<ToastVariant, LucideIcon> = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

export const TOAST_COLORS: Record<ToastVariant, string> = {
  success: "text-success border-success/30 bg-success/8",
  error: "text-destructive border-destructive/30 bg-destructive/8",
  warning: "text-warning border-warning/30 bg-warning/8",
  info: "text-primary border-primary/30 bg-primary/8",
};